import type { ImportRecordStatus, ImportSessionStatus } from '@/api/import-history';

/**
 * Libellés FR partagés de l'historique des imports (bloc « Données » de la
 * fiche école + page de détail d'un import).
 */

/** Étape du moteur d'import → libellé humain. */
export const STEP_LABELS: Record<string, string> = {
  import_levels: 'Niveaux',
  import_teachers: 'Enseignants',
  import_directors: 'Direction',
  import_parents: 'Parents',
  import_subjects: 'Matières',
  import_classes: 'Classes',
  import_students: 'Élèves',
  link_guardians: 'Liens parents-élèves',
};

/** Ordre d'affichage des étapes (même ordre que l'exécution). */
export const STEP_ORDER = [
  'import_levels',
  'import_teachers',
  'import_directors',
  'import_parents',
  'import_subjects',
  'import_classes',
  'import_students',
  'link_guardians',
];

/** Nom logique de fichier de l'assistant → libellé FR. */
export const LOGICAL_FILE_LABELS: Record<string, string> = {
  niveaux: 'Niveaux',
  matieres: 'Matières',
  classes: 'Classes',
  enseignants: 'Enseignants',
  direction: 'Direction',
  parents: 'Parents',
  eleves: 'Élèves',
};

/** Type d'entité tracé → libellé singulier. */
export const ENTITY_LABELS: Record<string, string> = {
  level: 'Niveau',
  teacher: 'Enseignant',
  director: 'Direction',
  guardian: 'Parent',
  subject: 'Matière',
  class: 'Classe',
  student: 'Élève',
  guardian_link: 'Lien parent-élève',
};

export const SESSION_STATUS_META: Record<
  ImportSessionStatus,
  { label: string; className: string }
> = {
  running: { label: 'En cours', className: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Terminé', className: 'bg-green-100 text-green-700' },
  completed_with_errors: {
    label: 'Terminé avec erreurs',
    className: 'bg-orange-100 text-orange-700',
  },
  failed: { label: 'Échoué', className: 'bg-red-100 text-red-700' },
};

export const RECORD_STATUS_META: Record<
  ImportRecordStatus,
  { label: string; className: string }
> = {
  created: { label: 'Créé', className: 'bg-green-100 text-green-700' },
  updated: { label: 'Mis à jour', className: 'bg-blue-100 text-blue-700' },
  skipped: { label: 'Ignoré', className: 'bg-gray-100 text-gray-600' },
  failed: { label: 'Échec', className: 'bg-red-100 text-red-700' },
};

/** Clé de payload connue → libellé FR (vue lisible). */
const FIELD_LABELS: Record<string, string> = {
  firstname: 'Prénom',
  lastname: 'Nom',
  email: 'Email',
  birthdate: 'Date de naissance',
  gender: 'Sexe',
  status: 'Statut du compte',
  sendPasswordEmail: 'Email de mot de passe',
  name: 'Nom',
  designation: 'Désignation',
  order: 'Ordre',
  code: 'Code',
  mefCode: 'Code MEF',
  cycleId: 'Cycle',
  levelId: 'Niveau',
  levelIds: 'Niveaux',
  levels: 'Niveaux',
  schoolClassId: 'Classe',
  classTeacherId: 'Professeur principal',
  establishmentId: 'Établissement',
  capacity: 'Capacité',
  type: 'Type',
  subjectGroupId: 'Groupe de matières',
  codeMatiere: 'Code matière (BCN)',
  teacherIds: 'Enseignants rattachés',
  coefficient: 'Coefficient',
  sourceUserRoleId: 'Parent (id V2)',
  targetUserRoleId: 'Élève (id V2)',
  relationLabel: 'Type de relation',
  guardianEmail: 'Email du parent',
  studentEmail: 'Email de l’élève',
  regroupement: 'Regroupement',
  code_niveau: 'Code niveau',
};

const looksLikeUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(value);

/**
 * Décode une valeur de payload pour la vue lisible. Renvoie null si la
 * valeur n'apporte rien à un non-technicien (ids V2 bruts) — la vue
 * technique affiche de toute façon le JSON complet.
 */
function decodeValue(key: string, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (key === 'gender') return value === 'M' ? 'Masculin' : value === 'F' ? 'Féminin' : String(value);
  if (key === 'status') {
    return value === 'INACTIVE' ? 'Inactif (aucune invitation envoyée)' : value === 'ACTIVE' ? 'Actif' : String(value);
  }
  if (key === 'sendPasswordEmail') return value ? 'Envoyé' : 'Non envoyé';
  if (key === 'birthdate' && typeof value === 'string') {
    const [y, m, d] = value.split('-');
    return y && m && d ? `${d}/${m}/${y}` : value;
  }
  if (key === 'levels' && Array.isArray(value)) {
    return `${value.length} niveau${value.length > 1 ? 'x' : ''} rattaché${value.length > 1 ? 's' : ''}`;
  }
  if (Array.isArray(value)) {
    const readable = value.filter((v) => typeof v !== 'string' || !looksLikeUuid(v));
    if (readable.length === 0) {
      return `${value.length} élément${value.length > 1 ? 's' : ''}`;
    }
    return readable.map(String).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'object') return null;
  const str = String(value);
  // Ids techniques V2 : sans intérêt en vue lisible.
  if (looksLikeUuid(str)) return null;
  return str;
}

/**
 * Payload JSON → paires libellé/valeur lisibles par tout le monde.
 */
export function decodePayload(
  payload: Record<string, unknown> | null,
): Array<{ label: string; value: string }> {
  if (!payload) return [];
  const out: Array<{ label: string; value: string }> = [];
  for (const [key, raw] of Object.entries(payload)) {
    const value = decodeValue(key, raw);
    if (value === null) continue;
    out.push({ label: FIELD_LABELS[key] ?? key, value });
  }
  return out;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
